import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  Typography
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';

import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import type { AnnouncementTestEmailDialogProps } from './AnnouncementTestEmailDialog.types';

export const AnnouncementTestEmailDialog = ({
  open,
  onClose,
  announcement
}: AnnouncementTestEmailDialogProps) => {
  const { handleSubmit, getValues, control } = useForm({
    defaultValues: { email: '' }
  });
  const handleError = useHandleError();

  const sendTestEmail = useMutation({
    mutationFn: ({
      emails,
      announcement_id
    }: {
      emails: string[];
      announcement_id: number;
    }) => {
      return axiosClient.post(`/announcements/send/test`, {
        emails,
        announcement_id
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to Send Test Email.');
    },
    onSuccess: (_, variables) => {
      enqueueSnackbar(`Test Email Sent ${variables.emails.length} User(s)`, {
        variant: 'success'
      });
      onClose();
    }
  });

  const onSubmit = () => {
    sendTestEmail.mutate({
      emails: getValues()
        .email.split(',')
        .map((e: string) => e.trim()),
      announcement_id: +announcement.id
    });
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={() => onClose()}>
      <DialogTitle>Send Test Email</DialogTitle>
      <DialogContent>
        <p>You can send an announcement as an email to test users.</p>
        <Box sx={{ pb: 2 }}>
          <strong>Announcement:</strong>
          <br />
          <Typography variant="h4">{announcement.title}</Typography>
        </Box>
        <form id="test-email-form" onSubmit={handleSubmit(onSubmit)}>
          <InputLabel htmlFor="email">Email Address:</InputLabel>
          <FormControl fullWidth>
            <Controller
              control={control}
              render={({ field, formState }) => (
                <TextField {...field} error={!!formState.errors.email} />
              )}
              rules={{
                required: true
              }}
              name="email"
            />
          </FormControl>
          <FormHelperText>Separate multiple emails by comma</FormHelperText>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" form="test-email-form">
          Send Test Email
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementTestEmailDialog;
